import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, TrendingUp, TrendingDown, ArrowDownToLine, ArrowUpFromLine, Target, Clock, Flag, Plus } from "lucide-react";
import { postRecommendations, getProfile } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, Loader, ScoreBar } from "../components/Primitives";
import AddHoldingModal from "../components/AddHoldingModal";
import { ratingColor, POS, NEG, fmtPct } from "../lib/format";

const ACTION_META = {
  Buy: { color: "#00e676", icon: ArrowDownToLine, label: "BUY" },
  Add: { color: "#00e5ff", icon: Plus, label: "ADD MORE" },
  Trim: { color: "#ff8f3b", icon: ArrowUpFromLine, label: "TRIM" },
  Sell: { color: "#ff3b30", icon: TrendingDown, label: "SELL" },
};
const BUCKET_COLOR = { Short: "#ffb300", Medium: "#00e5ff", Long: "#a855f7" };

export default function PortfolioRecos() {
  const { money, currency, usdInr } = useApp();
  const qc = useQueryClient();
  const [provider, setProvider] = useState("claude");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [addReco, setAddReco] = useState(null);
  const { data: prof } = useQuery({ queryKey: ["profile"], queryFn: getProfile });

  const run = useCallback(async () => {
    setLoading(true);
    try { setRes(await postRecommendations({ provider })); } finally { setLoading(false); }
  }, [provider]);
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const recos = (res?.recommendations || []).filter((r) =>
    filter === "all" ? true : filter === "buy" ? ["Buy", "Add"].includes(r.action) : ["Trim", "Sell"].includes(r.action));

  return (
    <div className="space-y-5">
      <Card className="p-5" testid="reco-controls">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#00e5ff]" /><div>
            <div className="text-sm font-semibold text-[#f8fafc]">Daily Buy / Sell Recommendations</div>
            <div className="text-xs text-[#64748b]">AI-reasoned, correlated to your portfolio & goals · entry, target & tenure for each idea</div>
          </div></div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-[#2d3c59] overflow-hidden" data-testid="reco-provider">
              {["claude", "gpt", "gemini"].map((p) => (
                <button key={p} data-testid={`reco-provider-${p}`} onClick={() => setProvider(p)}
                  className={`px-3 py-2 text-xs capitalize transition-colors ${provider === p ? "bg-[#1a2338] text-[#00e5ff]" : "text-[#64748b] hover:text-[#94a3b8]"}`}>{p}</button>
              ))}
            </div>
            <button data-testid="generate-reco-btn" onClick={run} disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-[#00e5ff] text-[#06121a] font-medium px-4 py-2.5 hover:bg-[#33eaff] disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> {loading ? "Analysing…" : "Refresh"}
            </button>
          </div>
        </div>
      </Card>

      {!loading && res && (
        <div className="flex items-center gap-3">
          {[["all", `All ${res.recommendations.length}`], ["buy", `Buy ${res.counts.buy}`], ["sell", `Sell/Trim ${res.counts.sell}`]].map(([k, l]) => (
            <button key={k} data-testid={`reco-filter-${k}`} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono transition-colors ${filter === k ? "bg-[#00e5ff] text-[#06121a]" : "border border-[#2d3c59] text-[#94a3b8] hover:text-[#f8fafc]"}`}>{l}</button>
          ))}
          <span className="text-[11px] text-[#64748b] font-mono ml-auto">{res.ai ? "AI reasoning" : "rule-based"} · via {res.provider}</span>
        </div>
      )}

      {loading ? <Loader label="Screening universe & reasoning over your goals…" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          <AnimatePresence>
            {recos.map((r, i) => {
              const meta = ACTION_META[r.action] || ACTION_META.Buy;
              const Icon = meta.icon;
              const isBuy = ["Buy", "Add"].includes(r.action);
              return (
                <motion.div key={r.instrument_id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className="p-5 h-full flex flex-col" testid={`reco-card-${r.instrument_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}1a` }}><Icon className="w-4.5 h-4.5" style={{ color: meta.color }} /></div>
                        <div>
                          <div className="font-semibold text-[#f8fafc] flex items-center gap-2">{r.name} {r.held && <span className="font-mono text-[10px] text-[#64748b]">HELD</span>}</div>
                          <div className="font-mono text-[11px] text-[#64748b]">{r.symbol} · {r.label}</div>
                        </div>
                      </div>
                      <Badge color={meta.color}>{meta.label}</Badge>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-4 font-mono text-center">
                      <div className="rounded-md border border-[#1e293b] py-2"><div className="overline">{isBuy ? "Entry" : "Exit ~"}</div><div className="text-sm text-[#f8fafc]">{r.currency === "USD" ? "$" : "₹"}{isBuy ? r.entry_price : r.target_price}</div></div>
                      <div className="rounded-md border border-[#1e293b] py-2"><div className="overline">{isBuy ? "Target" : "P&L"}</div><div className="text-sm" style={{ color: isBuy ? POS : (r.pnl_pct >= 0 ? POS : NEG) }}>{isBuy ? `${r.currency === "USD" ? "$" : "₹"}${r.target_price}` : fmtPct(r.pnl_pct || 0)}</div></div>
                      <div className="rounded-md border border-[#1e293b] py-2"><div className="overline">{isBuy ? "Stop" : "Score"}</div><div className="text-sm" style={{ color: isBuy ? NEG : ratingColor(r.rating) }}>{isBuy ? `${r.currency === "USD" ? "$" : "₹"}${r.stop_price}` : r.conviction}</div></div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] font-mono">
                      <span className="flex items-center gap-1 text-[#94a3b8]"><Clock className="w-3 h-3" /> {r.tenure}</span>
                      {r.goal_name && <span className="flex items-center gap-1" style={{ color: BUCKET_COLOR[r.goal_bucket] }}><Flag className="w-3 h-3" /> {r.goal_name} · {r.goal_bucket}-term</span>}
                      <span className="text-[#64748b] ml-auto">conv {r.conviction}</span>
                    </div>
                    <div className="mt-1.5"><ScoreBar value={r.conviction} color={ratingColor(r.rating)} /></div>

                    <div className="mt-3 space-y-2 flex-1">
                      <p className="text-sm text-[#cbd5e1] leading-relaxed">{r.thesis}</p>
                      {r.goal_fit && <p className="text-xs text-[#94a3b8] leading-relaxed"><span className="text-[#a855f7]">◎ Goal fit:</span> {r.goal_fit}</p>}
                      {r.risk && <p className="text-xs text-[#94a3b8] leading-relaxed"><span className="text-[#ffb300]">⚠ Risk:</span> {r.risk}</p>}
                    </div>

                    {isBuy && (
                      <button data-testid={`reco-add-${r.instrument_id}`} onClick={() => setAddReco(r)}
                        className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg border border-[#00e5ff]/40 text-[#00e5ff] py-2 text-sm hover:bg-[#00e5ff]/10 transition-colors">
                        <Plus className="w-4 h-4" /> Add to Portfolio under Goal
                      </button>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
          {recos.length === 0 && <Card className="p-10 text-center md:col-span-2 text-sm text-[#64748b]">No recommendations in this filter.</Card>}
        </div>
      )}
      <p className="text-center text-[11px] text-[#64748b]">Educational research generated from factor scores & your portfolio — not personalised investment advice.</p>

      <AnimatePresence>
        {addReco && (
          <AddHoldingModal
            prefill={{ instrument_id: addReco.instrument_id, name: addReco.name, currency: addReco.currency, price: addReco.entry_price, goal_id: addReco.goal_id }}
            goals={prof?.goals || []} lockInstrument
            title={`Buy ${addReco.name}`}
            onClose={() => setAddReco(null)}
            onSaved={() => { setAddReco(null); qc.invalidateQueries({ queryKey: ["portfolio"] }); qc.invalidateQueries({ queryKey: ["profile"] }); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
